'use client';

import { VoteType } from '@prisma/client';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Clock, MapPin, MessageSquare, Trash2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { memo, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { ReportWithDetails } from '../../../types/shared';
// 👇 Наши новые компоненты
import { TrafficVoteButtons } from './TrafficVoteButtons';
// 👇 Хук событий (убедись что он создан)

const EVENT_TYPE_STYLES: Record<string, { text: string; className: string }> = {
  ACCIDENT: { text: 'Авария', className: 'bg-red-500 text-white' },
  CONGESTION: { text: 'Затор', className: 'bg-amber-500 text-white' },
  ROAD_WORKS: { text: 'Ремонт', className: 'bg-blue-500 text-white' },
  OTHER: { text: 'Другое', className: 'bg-slate-500 text-white' },
};

type TrafficCardProps = {
  report: ReportWithDetails;
  likes: number;
  dislikes: number;

  onGoToMap: (coords: [number, number]) => void;
  onDeleteSuccess: (reportId: string) => void;

  onSelectId: (id: string) => void;
  isSelected: boolean;

  onVote: (reportId: string, voteType: VoteType) => void;
};

export const TrafficCard = memo(function TrafficCard({
  report,
  likes,
  dislikes,
  onGoToMap,
  onDeleteSuccess,
  onSelectId,
  isSelected,
  onVote,
}: TrafficCardProps) {
  const { data: session } = useSession();
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    setTimeAgo(formatDistanceToNow(new Date(report.createdAt), { addSuffix: true, locale: ru }));
  }, [report.createdAt]);

  const handleVote = useCallback(
    (voteType: VoteType) => {
      onVote(report.id, voteType);
    },
    [onVote, report.id],
  );

  const isAdmin = (session?.user as any)?.role === 'ADMIN';
  const eventStyle = EVENT_TYPE_STYLES[report.type] || EVENT_TYPE_STYLES.OTHER;
  const commentCount = report._count?.comments ?? report.comments.length;

  const handleDelete = useCallback(async () => {
    const t = toast.loading('Удаление...');
    try {
      const res = await fetch('/api/traffic/report/delete', {
        method: 'POST',
        body: JSON.stringify({ reportId: report.id }),
      });
      if (!res.ok) throw new Error();
      onDeleteSuccess(report.id);
      toast.success('Удалено', { id: t });
    } catch {
      toast.error('Ошибка', { id: t });
    }
  }, [report.id, onDeleteSuccess]);

  const handleSelect = useCallback(() => {
    onSelectId(report.id);
  }, [onSelectId, report.id]);

  return (
    <motion.div
      layoutId={isSelected ? undefined : `card-${report.id}`}
      onClick={handleSelect}
      className={`cursor-pointer group relative bg-slate-800/50 rounded-lg p-3 sm:p-4 border border-transparent hover:bg-slate-800 hover:border-slate-700 transition-all ${
        isSelected ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className="flex items-start gap-3">
        <Image
          src={report.author.image || '/default-avatar.jpg'}
          alt=""
          width={40}
          height={40}
          className="rounded-full w-10 h-10 mt-1"
          loading="lazy"
        />

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-bold text-white text-sm sm:text-base">{report.author.name}</p>
              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                <Clock size={12} /> {timeAgo}
              </p>
            </div>
            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${eventStyle.className}`}>
              {eventStyle.text}
            </span>
          </div>

          {report.comment && (
            <p className="mt-2 text-slate-200 text-sm leading-relaxed">{report.comment}</p>
          )}

          {report.imageUrl && (
            <div
              className="mt-3 relative h-32 w-full rounded-md overflow-hidden bg-black/20"
              onClick={(e) => {
                e.stopPropagation();
                setImageModalOpen(true);
              }}
            >
              <Image
                src={report.imageUrl}
                alt="Фото"
                fill
                className="object-cover hover:scale-105 transition-transform"
                sizes="(max-width: 768px) 100vw, 300px"
                loading="lazy"
              />
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/5">
            {/* ✅ Наши кнопки */}
            <TrafficVoteButtons
              likes={likes} // ✅ вместо report.likes
              dislikes={dislikes} // ✅ вместо report.dislikes
              myVote={report.myVote}
              onVote={handleVote}
              variant="card"
            />

            <div className="flex gap-3 text-slate-400">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onGoToMap([report.latitude, report.longitude]);
                }}
                className="hover:text-sky-400 p-1"
              >
                <MapPin size={18} />
              </button>
              <div className="flex items-center gap-1">
                <MessageSquare size={18} /> <span>{commentCount}</span>
              </div>
              {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  className="hover:text-red-400 p-1"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isImageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent
          className="max-w-4xl bg-transparent border-0 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <VisuallyHidden>
            <DialogTitle>Image</DialogTitle>
          </VisuallyHidden>
          {report.imageUrl && (
            <Image
              src={report.imageUrl}
              alt=""
              width={1000}
              height={800}
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
});
